import React, { createContext, useMemo, useContext } from 'react';
import useUAParser from 'hooks/useUAParser';
import { Theme, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/styles';
import canUseDOM from 'utils/canUseDOM';

interface ResponsiveQuery {
  isMobile: boolean;
}

const defaultValue: ResponsiveQuery = { isMobile: false };

type ReponsiveQueryHook = () => ResponsiveQuery;

const ResponsiveQueryContext = createContext(defaultValue);

/**
 * Compute the responsive query it one place and provide the same value to
 * all components that needs it
 *
 * This helps with maintaining a consistent value across the app.
 *
 * Otherwise, every component that make use of this will have to recompute
 * the value. Because MUI's useMediaQuery cannot distinguish between a
 * SSR rehydrate vs a normal render, it must always return the default value
 * for SSR during the first render, and then in an effect compute the value.
 *
 * This means that for many components this value will be wrong for one render
 * before being corrected. This is not acceptable in many cases.
 *
 * Calling useMediaQuery in one place high up in the hierarchy has the benefit
 * of having the incorrect value only once on load, and all subsequent usage
 * of it will have the correct values.
 */
const ResponsiveQueryProvider: React.FC = ({ children }) => {
  const uaProps = useUAParser();
  const theme = useTheme<Theme>();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Use media query with fallback for SSR
  const value: ResponsiveQuery = useMemo(
    () => ({
      isMobile: canUseDOM ? isMobile : uaProps.device.type === 'mobile',
    }),
    [isMobile, uaProps.device.type],
  );

  return (
    <ResponsiveQueryContext.Provider value={value}>
      {children}
    </ResponsiveQueryContext.Provider>
  );
};

const useResponsiveQuery: ReponsiveQueryHook = () => {
  const query = useContext(ResponsiveQueryContext);

  return query;
};

export default useResponsiveQuery;
export { ResponsiveQueryProvider };
